const { idToParts, partsWithoutName } = require('./index')

class PgGraph {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async getLocationIdsInGeoLocation (id) {
    const idParts = idToParts(id)
    const geoId = partsWithoutName(idParts).join(':')
    const {rows} = await this.pgConnection.query(`
      WITH RECURSIVE normalized_locations AS (
        SELECT DISTINCT
          fsid,
          CASE
            WHEN fsid = 'national'
              THEN 'country:ng'
            WHEN fsid NOT LIKE 'country:%'
              THEN 'country:ng:' || fsid
            ELSE fsid
          END AS node_id
        FROM avocado.data_location
        where coalesce(active, false) is true
      ), 
      location_parts AS (
        SELECT
          fsid,
          node_id,
          regexp_replace(
            node_id,
            ':(sdp|name):.*$',
            ''
          ) AS lga_node_id,
      
          regexp_replace(
            node_id,
            ':(sdp|name|lga):.*$',
            ''
          ) AS state_node_id,
      
          regexp_replace(
            node_id,
            ':(sdp|name|lga|state):.*$',
            ''
          ) AS geo_node_id,
      
          regexp_replace(
            node_id,
            ':(sdp|name|lga|state|zone):.*$',
            ''
          ) AS country_node_id
      
        FROM normalized_locations
      ),
      
      graph_edges AS (
        SELECT DISTINCT
          edge.parent_node_id,
          edge.child_node_id
        FROM location_parts lp
        CROSS JOIN LATERAL (
          VALUES
            (lp.country_node_id, lp.geo_node_id),
            (lp.geo_node_id,     lp.state_node_id),
            (lp.state_node_id,   lp.lga_node_id),
            (lp.lga_node_id,     lp.node_id)
        ) AS edge(parent_node_id, child_node_id)
        -- Handles nodes that skip a hierarchy level.
        WHERE edge.parent_node_id <> edge.child_node_id
      ),
      descendants AS (
        SELECT
          e.parent_node_id,
          e.child_node_id,
          1 AS depth,
          ARRAY[e.parent_node_id, e.child_node_id] AS path
        FROM graph_edges e
        WHERE e.parent_node_id = $1
      
        UNION ALL
      
        SELECT
          d.child_node_id,
          e.child_node_id,
          d.depth + 1,
          d.path || e.child_node_id
        FROM descendants d
        JOIN graph_edges e
          ON e.parent_node_id = d.child_node_id
        WHERE NOT e.child_node_id = ANY(d.path)
      )

      SELECT nl.fsid
      FROM normalized_locations nl
      WHERE nl.node_id = $1

      UNION

      SELECT nl.fsid
      FROM descendants d
      INNER JOIN normalized_locations nl ON d.child_node_id = nl.node_id
    `, [geoId])

    return rows.map(r => r.fsid)
  }

  async getDescendantSupplierIds (fsIds) {
    const { rows } = await this.pgConnection.query(`
      WITH target_locations AS (
        SELECT
          CASE
            WHEN fsid = 'national' THEN 'country:ng'
            WHEN fsid NOT LIKE 'country:%' THEN 'country:ng:' || fsid
            ELSE fsid
          END AS node_id,
          l.uuid
        FROM avocado.data_location l
        WHERE fsid = ANY($1)
      ),
      regions AS (
        SELECT DISTINCT region.region_id
        FROM target_locations t
        CROSS JOIN LATERAL (
          VALUES
            (regexp_replace(t.node_id, ':(sdp|name):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga|state):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga|state|zone):.*$', ''))
        ) AS region(region_id)
      ),
      rule_keys AS (
        -- Match already-normalized rules.
        SELECT region_id AS relationship_rule
        FROM regions

        UNION

        -- Match legacy rules without the country prefix.
        SELECT regexp_replace(region_id, '^country:ng:', '')
        FROM regions
        WHERE region_id LIKE 'country:ng:%'
      )
      SELECT l.fsid
      FROM rule_keys k
      JOIN avocado.data_location_relationship_rule rr
        ON rr.relationship_rule_type = 'suppliesStateSDPs'
        AND rr.relationship_rule = k.relationship_rule
      JOIN avocado.data_location l
        ON l.uuid = rr.location_id

      UNION

      SELECT supplier.fsid
      FROM target_locations target
      JOIN avocado.data_location_relationship relationship
        ON relationship.location_id = target.uuid
        AND relationship.relationship_type = 'suppliedBy'
      JOIN avocado.data_location supplier
        ON supplier.uuid = relationship.relationship
    `, [fsIds])

    return rows.map(r => r.fsid)
  }

  async getDescendantSupplieeIds (fsid) {
    const { rows } = await this.pgConnection.query(`
      WITH target_regions AS MATERIALIZED (
        SELECT DISTINCT
          CASE
            WHEN lr.relationship_rule NOT LIKE 'country:%'
              THEN 'country:ng:' || lr.relationship_rule
            ELSE lr.relationship_rule
          END AS region_id
        FROM avocado.data_location AS source
        JOIN avocado.data_location_relationship_rule AS lr
          ON lr.location_id = source.uuid
        WHERE source.fsid = $1
          AND lr.relationship_rule IS NOT NULL
      ),
      normalized_locations AS (
        SELECT
          l.fsid,
          CASE
            WHEN l.fsid = 'national' THEN 'country:ng'
            WHEN l.fsid NOT LIKE 'country:%' THEN 'country:ng:' || l.fsid
            ELSE l.fsid
          END AS node_id
        FROM avocado.data_location AS l
      )
      SELECT nl.fsid
      FROM normalized_locations AS nl
      WHERE EXISTS (
        SELECT 1
        FROM target_regions AS tr
        WHERE starts_with(nl.node_id, tr.region_id || ':')
      )
    `, [fsid])

    return rows.map(r => r.fsid)
  }

  async getSiblingLocationIds (fsid) {
    const { rows } = await this.pgConnection.query(`
      WITH target_locations AS (
        SELECT
          CASE
            WHEN fsid = 'national' THEN 'country:ng'
            WHEN fsid NOT LIKE 'country:%' THEN 'country:ng:' || fsid
            ELSE fsid
          END AS node_id,
          l.supply_chain_level
        FROM avocado.data_location l
        left join avocado.data_location_relationship lr on lr.location_id = l.uuid
        WHERE fsid = $1 and lr.id is null
      ),
      regions AS (
        SELECT DISTINCT region.region_id, supply_chain_level
        FROM target_locations t
        CROSS JOIN LATERAL (
          VALUES
            (regexp_replace(t.node_id, ':(sdp|name):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga|state):.*$', '')),
            (regexp_replace(t.node_id, ':(sdp|name|lga|state|zone):.*$', ''))
        ) AS region(region_id)
      ),
      rule_keys AS (
        select * from (
          -- Match already-normalized rules.
          SELECT region_id AS relationship_rule, supply_chain_level
          FROM regions

          UNION

          -- Match legacy rules without the country prefix.
          SELECT regexp_replace(region_id, '^country:ng:', ''), supply_chain_level 
          FROM regions
        ) as rel
      ),
      suppliers as (
        SELECT DISTINCT l.fsid, l.uuid, k.supply_chain_level, rr.*
        FROM rule_keys k
         JOIN avocado.data_location_relationship_rule rr
           ON rr.relationship_rule_type = 'suppliesStateSDPs'
         AND rr.relationship_rule = k.relationship_rule
         JOIN avocado.data_location l
          ON l.uuid = rr.location_id
      ),
      target_regions AS MATERIALIZED (
        SELECT DISTINCT
          CASE
            WHEN lr.relationship_rule NOT LIKE 'country:%'
              THEN 'country:ng:' || lr.relationship_rule
            ELSE lr.relationship_rule
          END AS region_id,
          source.supply_chain_level 
        FROM suppliers AS source
        JOIN avocado.data_location_relationship_rule AS lr
          ON lr.location_id = source.uuid
        WHERE lr.relationship_rule IS NOT null
      ),
      normalized_locations AS (
        SELECT
          l.fsid,
          CASE
            WHEN l.fsid = 'national' THEN 'country:ng'
            WHEN l.fsid NOT LIKE 'country:%' THEN 'country:ng:' || l.fsid
            ELSE l.fsid
          END AS node_id,
          l.supply_chain_level as nl_supply_chain_level
        FROM avocado.data_location AS l
      )
      SELECT nl.fsid
      FROM normalized_locations AS nl
      WHERE nl.fsid <> $1
        AND EXISTS (
        SELECT 1
        FROM target_regions AS tr
        WHERE (
          nl.node_id = tr.region_id
          OR starts_with(nl.node_id, tr.region_id || ':')
        )
          AND nl.nl_supply_chain_level = tr.supply_chain_level
      )`,
    [fsid])

    return rows.map(r => r.fsid)
  }
}

module.exports = {
  PgGraph
}
